import { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Timer, Settings2 } from 'lucide-react';
import type { ApiSwatidRelay } from '@/types/api';
import { formatTimerRemaining } from '@/utils/swatidValidation';
import { getSwatidRelayDisplayName, isCustomSwatidRelayName } from '@/utils/swatidRelay';
import { cn } from '@/lib/utils';
import type { SwatidOperationMode } from '@/constants/swatid';
import { SwatidRelayDetailDialog } from './SwatidRelayDetailDialog';
import { SwatidRelayToggleButton } from './SwatidRelayToggleButton';

interface SwatidRelayGridProps {
  controllerId: string;
  relays: ApiSwatidRelay[];
  canControl: boolean;
  isOnline: boolean;
  currentMode?: SwatidOperationMode;
}

export function SwatidRelayGrid({
  controllerId,
  relays,
  canControl,
  isOnline,
  currentMode,
}: SwatidRelayGridProps) {
  const [selectedRelay, setSelectedRelay] = useState<ApiSwatidRelay | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    if (!selectedRelay) return;
    const fresh = relays.find((r) => r.relayNumber === selectedRelay.relayNumber);
    if (fresh) setSelectedRelay(fresh);
  }, [relays, selectedRelay?.relayNumber]);

  const openRelay = (relay: ApiSwatidRelay) => {
    setSelectedRelay(relay);
    setDetailOpen(true);
  };

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {relays.map((relay) => (
          <Card
            key={relay.relayNumber}
            className={cn(
              'p-3 transition-colors',
              relay.currentState && 'border-success/40 bg-success/5',
              !canControl && 'cursor-pointer hover:border-primary/50'
            )}
            onClick={() => !canControl && openRelay(relay)}
          >
            <div className="flex items-start justify-between gap-1 mb-2">
              <span className="text-xs font-medium text-muted-foreground">
                R{relay.relayNumber}
              </span>
              <Badge
                variant={relay.currentState ? 'default' : 'secondary'}
                className={cn('text-[10px] h-5', relay.currentState && 'bg-success')}
              >
                {relay.currentState ? 'ON' : 'OFF'}
              </Badge>
            </div>
            <p className="text-sm font-medium truncate mb-2" title={getSwatidRelayDisplayName(relay)}>
              {getSwatidRelayDisplayName(relay)}
            </p>
            {isCustomSwatidRelayName(relay) && (
              <p className="text-[10px] text-primary mb-1">Nombre personalizado</p>
            )}
            {relay.timerRemaining > 0 && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                <Timer className="w-3 h-3" />
                {formatTimerRemaining(relay.timerRemaining)}
              </p>
            )}

            {canControl && isOnline ? (
              <div className="flex gap-1 mt-1">
                <SwatidRelayToggleButton
                  controllerId={controllerId}
                  relay={relay}
                  className="flex-1 h-8 text-xs"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    openRelay(relay);
                  }}
                  title="Configuración"
                >
                  <Settings2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-1 h-7 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  openRelay(relay);
                }}
              >
                <Settings2 className="w-3 h-3 mr-1" />
                Configurar
              </Button>
            )}
          </Card>
        ))}
      </div>

      {!canControl && isOnline && (
        <p className="text-xs text-muted-foreground mt-3 italic">
          {currentMode === 'manual'
            ? 'En modo Manual los relés se controlan con interruptores físicos.'
            : currentMode === 'local'
              ? 'En modo Local los relés se ejecutan por programaciones. Cambie a Remota para control API.'
              : 'Cambie a modo Remota para controlar relés desde la plataforma.'}
        </p>
      )}

      <SwatidRelayDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        controllerId={controllerId}
        relay={selectedRelay}
        canControl={canControl && isOnline}
      />
    </>
  );
}
